import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Environment } from "../../../types";
import { CHAIN_IDS } from "../../../lib/constants";
import { getApiBase } from "../../../lib/remote-config";

export const CHAIN_INFO_QUERY_KEY = "send:chain-info";

interface ChainExplorerPostfix {
  address?: string;
  token?: string;
  transaction?: string;
}

interface ChainInfo {
  chain: string;
  chain_id: number;
  currency_decimals: number;
  currency_name: string;
  currency_symbol: string;
  explorer_url?: string;
  info_url: string;
  name: string;
  network_id: number;
  rpc: string;
  short_name: string;
  testnet: boolean;
  explorer_postfix?: ChainExplorerPostfix;
}

interface ChainInfoResponse {
  code?: number;
  message?: string;
  // wallet API 의 /v1/public/chain/info/{chainId} 는 단일 객체가 아니라
  // 항상 길이 1의 배열을 반환한다. 과거 단일 객체 형태와도 호환되게 둘 다 받는다.
  data?: ChainInfo | ChainInfo[];
}

function getChainInfoPayload(json: ChainInfoResponse | ChainInfo): ChainInfo {
  if ("data" in json && json.data !== undefined) {
    const payload = json.data;
    if (Array.isArray(payload)) {
      const first = payload[0];
      if (!first) throw new Error("Chain info payload is empty.");
      return first;
    }
    return payload;
  }
  return json as ChainInfo;
}

function joinExplorerPath(base: string, ...segments: string[]): string {
  const normalizedBase = base.replace(/\/+$/, "");
  const normalizedSegments = segments
    .map((segment) => segment.replace(/^\/+|\/+$/g, ""))
    .filter(Boolean);

  return [normalizedBase, ...normalizedSegments].join("/");
}

function buildTransactionExplorerUrl(
  env: Environment,
  chainId: number,
  chain: ChainInfo | undefined,
  txHash: string | undefined,
): string | undefined {
  if (!txHash) return undefined;

  if (chainId === CHAIN_IDS[env].cross) {
    return getCrossTransactionExplorerUrl(chainId, txHash);
  }

  if (!chain?.explorer_url) {
    return getFallbackTransactionExplorerUrl(env, chainId, txHash);
  }

  return joinExplorerPath(
    chain.explorer_url,
    chain.explorer_postfix?.transaction ?? "tx",
    txHash,
  );
}

function getCrossTransactionExplorerUrl(
  chainId: number,
  txHash: string,
): string {
  return joinExplorerPath(
    "https://explorer.onechain.nexus",
    String(chainId),
    "tx",
    txHash,
  );
}

function getFallbackTransactionExplorerUrl(
  env: Environment,
  chainId: number,
  txHash: string,
): string | undefined {
  const chains = CHAIN_IDS[env];

  if (chainId === chains.cross) {
    return getCrossTransactionExplorerUrl(chainId, txHash);
  }

  if (chainId === chains.bsc) {
    const base =
      env === "production"
        ? "https://bscscan.com"
        : "https://testnet.bscscan.com";
    return joinExplorerPath(base, "tx", txHash);
  }

  return undefined;
}

export function useTransactionExplorerUrl(
  env: Environment,
  chainId: number,
  txHash: string | undefined,
  enabled = true,
) {
  const query = useQuery<ChainInfo>({
    queryKey: [CHAIN_INFO_QUERY_KEY, env, chainId],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const res = await fetch(`${baseUrl}/v1/public/chain/info/${chainId}`, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) throw new Error(`Failed to fetch chain info: ${res.status}`);

      const json = (await res.json()) as ChainInfoResponse | ChainInfo;
      if ("code" in json && json.code !== undefined && json.code !== 200) {
        throw new Error(`Chain info API error: ${json.code}`);
      }

      return getChainInfoPayload(json);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const chainInfo = query.data;
  const transactionUrl = useMemo(
    () => buildTransactionExplorerUrl(env, chainId, chainInfo, txHash),
    [chainId, chainInfo, env, txHash],
  );

  return { transactionUrl, chainInfo, ...query };
}

/**
 * 체인 정보를 1회 조회한 뒤, txHash별 익스플로러 URL을 만들어 주는 빌더를 반환.
 * 거래 이력 리스트처럼 행마다 URL이 필요한 곳에서 훅 규칙을 어기지 않고 쓰기 위한 헬퍼.
 */
export function useExplorerUrlBuilder(
  env: Environment,
  chainId: number,
  enabled = true,
) {
  const { chainInfo } = useTransactionExplorerUrl(
    env,
    chainId,
    undefined,
    enabled,
  );
  const buildTxUrl = useMemo(
    () => (txHash: string | undefined) =>
      buildTransactionExplorerUrl(env, chainId, chainInfo, txHash),
    [env, chainId, chainInfo],
  );
  return { buildTxUrl, chainInfo };
}
